import React from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'src/shared/components/tooltip'
import { twMerge } from 'tailwind-merge'
import { CheckmarkIcon } from '../../icons/checkmark-icon'

export interface CheckboxNewProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'onChange'
  > {
  wrapperClassName?: string
  label?: React.ReactNode
  skipTranslation?: boolean
  skipTranslationTooltip?: boolean
  tooltipText?: string
  icon?: React.ReactNode
  onChange: (checked: boolean) => void
}

export const CheckboxNew: React.FC<CheckboxNewProps> = ({
  wrapperClassName = '',
  className = '',
  checked,
  onChange,
  label = '',
  skipTranslation = false,
  skipTranslationTooltip = false,
  tooltipText,
  icon,
  ...props
}) => {
  const { t } = useTranslation()
  return (
    <span
      id={props.id}
      className={`flex items-center gap-2 ${wrapperClassName}`}
    >
      <label
        className={twMerge(
          'group relative flex cursor-pointer items-center gap-2',
          props.disabled && 'cursor-default',
        )}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange(e.target.checked)
          }
          className={twMerge(
            'h-5 w-5 shrink-0 cursor-pointer appearance-none border-2',
            'rounded-[2px] border-border200',
            'checked:bg-primary-500 group-hover:border-primary-500',
            'group-hover:checked:bg-primary-600',
            'checked:border-primary-500 group-hover:checked:border-primary-600',
            'disabled:cursor-default disabled:border-bgTertiary disabled:checked:border-bgTertiary',
            'disabled:checked:bg-bgTertiary disabled:checked:hover:border-bgTertiary',
            'disabled:checked:hover:bg-bgTertiary disabled:group-hover:border-bgTertiary',
            'transition-all duration-150 focus:outline-none',
            className,
          )}
          {...props}
        />
        {checked && (
          <CheckmarkIcon className="absolute left-0.5 shrink-0 stroke-iconOnColor" />
        )}
        {label && (
          <span className="text-M">
            {!skipTranslation && typeof label === 'string' ? t(label) : label}
          </span>
        )}
      </label>
      {icon && icon}
      {tooltipText && (
        <Tooltip
          message={tooltipText}
          skipTranslation={skipTranslationTooltip}
        />
      )}
    </span>
  )
}
